import { useKeepAwake } from 'expo-keep-awake'
import { observer } from 'mobx-react-lite'
import React, { ComponentProps, useCallback, useEffect, useMemo, useState } from 'react'
import {
  AppState,
  AppStateStatus,
  Modal,
  StatusBar,
  StyleSheet,
  View,
  Platform,
  useWindowDimensions
} from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

import { BottomSheetFooter, BottomSheetHandle, BottomSheetHandleProps, BottomSheetModal } from '@gorhom/bottom-sheet'
import { PlayerKind } from '@ppwr/poker'

import { PlayerCards, PlayerHand, Shortcuts, Table, WalkthroughOverlay } from '../../components/ANewDesign'
import { BottomModalContent } from '../../components/ANewDesign/BottomModalContent'
import { BottomModalType, BottomModalTypes, ChatFooter } from '../../components/ANewDesign/BottomModalContent/Content'
import { ModalContent } from '../../components/ANewDesign/ModalContent'
import { BlindsTable } from '../../components/ANewDesign/Table/BlindsTable/BlindsTable'
import { Top } from '../../components/ANewDesign/WalkthroughOverlay/Top'
import { LineProgress } from '../../components/LineProgress/LineProgress'
import { useActualHeight } from '../../hooks/use-actual-height'
import { useStore } from '../../models'
import { palette } from '../../theme/palette'
import { useConnectToGame, useGameModals, useShortcuts, useVibration } from './hooks'
import { useGameBottomModal } from './hooks/useGameBottomModals'
import { useCelebration } from './hooks/useCelebration'
import { usePreFold } from './hooks/usePreFold'
import { useShowLive } from './hooks'
import { LiveVideoPreview } from '../../components/ANewDesign/LiveVideoPreview'
import { events } from '../../models/analytics/analytics-events'
import { GameModalKinds } from '../../models/game/game-modals'
import { TeacherControl } from '../../components/ANewDesign/TeacherControl/TeacherControl'
import { DS } from '../../design-system'
import Animated, { ReduceMotion, useAnimatedStyle } from 'react-native-reanimated'
import { playSound } from '../../utils/audioService'
import { ChatHandle } from '../../components/ANewDesign/BottomModalContent/Content/Chats/ChatHandle'
import { INPUT_HEIGHT } from '../../components/ANewDesign/BottomModalContent/Content/Chats/partials/ChatTextInput'
import { ToastNotification } from '../../components/ANewDesign/ToastNotification/ToastNotification'
import { useListenGameToastNotifications } from './hooks/useListenGameToastNotifications'
import { useMaxWidth, WebWrapper } from '@/components/WebWrapper'
import { useDrawerStore } from '@/components/Drawer/Drawer'
import { useFocusEffect, useLocalSearchParams, useNavigation, useRouter } from 'expo-router'
import { AVPermissionsModal } from '../../components/ANewDesign/ModalContent/Content/AVPermissions/AVPermissionsModal'
import { getPlayersColumns, sortPlayerStartingByMe } from '../../components/ANewDesign/Table/utils'
import { useResponsive } from '@/screens/new-design/hooks'
import { CommandsContextProvider } from './hooks/useCommonCommands'

const isWeb = Platform.OS === 'web'

export const GameScreen = observer(() => {
  useKeepAwake(undefined, { suppressDeactivateWarnings: true })
  const { renderEmojis, clearEmojis } = useCelebration()
  const {
    game: {
      gameKind,
      isGamePlaying,
      isGameReadyToPlay,
      myPlayer,
      isMyPlayerObserver,
      showChats,
      seatedPlayersWithSeatedObserver,
      setShowChats,
      seatedPlayers,
      isGameReset,
      isTournamentModeOn,
      createBuyPrompt,
      hasUserDeclinedToBuy,
      isMyPlayerOutOfChips,
      isMyPlayerOut,
      modals: { playerActionTimerExpired },
      shouldPlayChipsSound,
      setShouldPlayChipsSound,
      isWalkthrough,
      currentStep,
      isPracticeMode,
      isThreeCard,
      bots,
      createEndPracticeGamePrompt,
      shouldPlayerSeeStartBottomModal,
      startGame,
      walkthroughType,
      isGameOver,
      timerCountdown,
      isHandWinnersEvent,
      isGamePaused,
      isMyPlayerTeacher
    },
    network,
    communication,
    messages: { clearSnack, snack },
    analytics: { setEvent },
    permissions: { isSoundEnabled }
  } = useStore()
  const { verticalScale } = useResponsive()
  const insets = useSafeAreaInsets()

  const navigation = useNavigation()
  const isDrawerOpen = useDrawerStore().status === 'open'

  const { modalConfig, setModalConfig, onModalClose } = useGameModals()

  const sortedPlayers = sortPlayerStartingByMe(seatedPlayers, myPlayer)
  const playersColumn = getPlayersColumns(sortedPlayers)

  const isNinePlayerInTable = playersColumn.center[0]

  const { code } = useLocalSearchParams<{
    code?: string
  }>()
  const router = useRouter()

  const {
    bottomModalContent,
    isBottomModalOpen,
    closeBottomModal,
    handleOpenChats,
    handleOpenGameStartModal,
    currentPosition,
    animatedPosition
  } = useGameBottomModal()

  const [playerHandHeight, setPlayerHandeHeight] = useState(0)
  const [teacherControlsButtonHeight, setTeacherControlsButtonHeight] = useState(0)
  const dimensions = useWindowDimensions()

  const shouldOffetAgainstGameStartModal =
    bottomModalContent?.kind === BottomModalTypes.GameStart && currentPosition !== -1

  const teacherControllAnimatedOffset = useAnimatedStyle(() => {
    if (shouldOffetAgainstGameStartModal) {
      return {
        bottom: -animatedPosition.value + dimensions.height
      }
    }
    return {
      bottom: playerHandHeight
    }
  })

  const isFocused = navigation.isFocused()

  const { handleVideo } = useShortcuts()

  useVibration()
  usePreFold()
  useShowLive(handleVideo, onModalClose, modalConfig)
  useListenGameToastNotifications()

  useEffect(() => {
    if (shouldPlayChipsSound && isSoundEnabled) {
      setShouldPlayChipsSound(false)
      playSound('pokerChips')
    }
  }, [shouldPlayChipsSound, setShouldPlayChipsSound, isSoundEnabled])

  useEffect(() => {
    communication.clearShowCards()
  }, [isGameReset])

  const channelName = communication.channel?.channelName
  const isWebSocketConnected = network.isWebSocketConnected

  const HEIGHT = useActualHeight()
  const isMediumDevice = HEIGHT < 900 && HEIGHT > 670
  const isBigDevice = HEIGHT >= 900

  const playerHandHeightMap = {
    DealTheFlop: {
      medium: 1.7,
      small: isWeb ? 2.5 : 1.9,
      big: 1.4
    },
    RaiseOrFold: {
      medium: 1,
      small: 1.2,
      big: 0.8
    },
    TeacherView: {
      medium: 0.5,
      small: 0.8,
      big: 0.5
    },
    default: {
      medium: Platform.OS === 'android' ? 1 : 1.2,
      small: 1.5,
      big: 1
    }
  }

  const getPlayerHandHeight = () => {
    let type: 'small' | 'medium' | 'big' = 'small' // default size
    if (isMediumDevice) {
      type = 'medium'
    }
    if (isBigDevice) {
      type = 'big'
    }

    if (isMyPlayerObserver && isMyPlayerTeacher) return playerHandHeightMap.TeacherView[type]
    if (!isWalkthrough || walkthroughType === 'TableTour') return playerHandHeightMap.default[type]
    if (isWalkthrough && (walkthroughType === 'DealTheFlop' || walkthroughType === 'RaiseOrFold'))
      return playerHandHeightMap[walkthroughType][type]
  }

  useConnectToGame({ isWebSocketConnected, channelName })

  useEffect(() => {
    if (isGamePlaying) {
      clearSnack()
    }
  }, [isGamePlaying, clearSnack])

  useEffect(() => {
    if (!isGameReadyToPlay || isWalkthrough) return

    if (
      shouldPlayerSeeStartBottomModal &&
      !isBottomModalOpen &&
      !isDrawerOpen &&
      !showChats &&
      timerCountdown !== undefined
    ) {
      handleOpenGameStartModal()
    } else if (isThreeCard && bots.length === 2) {
      ;(async () => await startGame())()
    }
  }, [
    gameKind,
    isGameReadyToPlay,
    isBottomModalOpen,
    snack?.type,
    handleOpenGameStartModal,
    isDrawerOpen,
    showChats,
    isWalkthrough,
    shouldPlayerSeeStartBottomModal,
    isThreeCard,
    bots,
    startGame
  ])

  useEffect(() => {
    if (
      ((isGamePlaying && isBottomModalOpen) || showChats) &&
      bottomModalContent?.kind === BottomModalTypes.GameStart
    ) {
      closeBottomModal()
      if (showChats) {
        setTimeout(() => {
          handleOpenChats()
        }, 500)
      }
    }
  }, [bottomModalContent?.kind, closeBottomModal, isBottomModalOpen, isGamePlaying, showChats, handleOpenChats, snack])

  useEffect(() => {
    if (bottomModalContent?.kind !== BottomModalTypes.Chats && showChats) {
      setShowChats(false)
    }
  }, [bottomModalContent?.kind, setShowChats, showChats])

  useFocusEffect(
    useCallback(() => {
      StatusBar.setBarStyle(isDrawerOpen ? 'dark-content' : 'light-content', true)

      return () => {
        StatusBar.setBarStyle('dark-content', true)
      }
    }, [isDrawerOpen])
  )

  useEffect(() => {
    if (seatedPlayers.length < 2) clearSnack()
  }, [clearSnack, seatedPlayers.length])

  useEffect(() => {
    if (isMyPlayerOut) {
      const playerOutOfChips = seatedPlayers.filter((p) => !p.chips && p.buyIn && p?.kind === PlayerKind.HUMAN)

      let botsOutChips = false

      if (isPracticeMode) {
        botsOutChips = bots.every((bot) => bot.chips === 0 && bot.buyIn)
        if (botsOutChips) {
          // 02/04/2025 - Removing this as per Shelby's and Bettina's request to show the game stats button.
          /*setTimeout(() => {
            createEndPracticeGamePrompt()
          }, 1000)*/
        }
      }

      if ((playerOutOfChips.length > 0 || botsOutChips) && isTournamentModeOn === false) {
        //Note: Show buy-in popup for player(s) goes out of chips. Show pause timer for player having chips
        if (isMyPlayerOutOfChips && !hasUserDeclinedToBuy && isMyPlayerOut) {
          createBuyPrompt()
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMyPlayerOut])

  useEffect(() => {
    if (playerActionTimerExpired && bottomModalContent?.kind === BottomModalTypes.Bet) {
      closeBottomModal()
    }
  }, [bottomModalContent?.kind, closeBottomModal, playerActionTimerExpired])

  useEffect(() => {
    if (isFocused) {
      clearEmojis()
    }
  }, [isFocused, clearEmojis])

  const onChange = useCallback(
    (nextAppState: AppStateStatus) => {
      if (nextAppState === 'active') {
        if (bottomModalContent?.kind === 'ShowCards') {
          closeBottomModal()
        }
      }
      if (nextAppState === 'active' && modalConfig?.kind !== 'options' && modalConfig?.kind !== 'appPermission') {
        setModalConfig(undefined)
      }
    },
    [setModalConfig, modalConfig?.kind, bottomModalContent?.kind, closeBottomModal]
  )

  useEffect(() => {
    const appStateListener = AppState.addEventListener('change', onChange)
    return () => {
      appStateListener.remove()
    }
  }, [onChange])

  useEffect(() => {
    if (modalConfig?.kind === GameModalKinds.buy) {
      setEvent(events.game.add_chips_modal_viewed)
    }
  }, [modalConfig, setEvent])

  useEffect(() => {
    if (modalConfig?.kind === GameModalKinds.buy && !isMyPlayerOutOfChips) {
      onModalClose()
    }
  }, [modalConfig, isMyPlayerOutOfChips, onModalClose])

  useEffect(() => {
    setTimeout(() => {
      if (code) {
        router.push(`/game/join/${code}`)
      }
    }, 200)
  }, [code, router])

  useEffect(() => {
    setEvent(events.gamescreen.game_screen_viewed, {
      game_kind: gameKind
    })
  }, [setEvent])

  // useEffect(() => {
  //   if (isGameOver) {
  //     closeBottomModal()
  //   }
  // }, [isGameOver, closeBottomModal])

  // TODO: Why is Android returning 0 here? Adding 20 as default padding.
  const paddingBottom = insets.bottom > 0 ? insets.bottom : Platform.OS === 'web' ? 0 : 20

  const showPlayerCards = !(isMyPlayerObserver || isMyPlayerOut || isHandWinnersEvent || isGameReset)

  const showLoadingProgressBar =
    (!isWebSocketConnected || seatedPlayersWithSeatedObserver.length === 0) && !isWalkthrough

  return (
    <View style={{ flex: 1, paddingBottom: insets.bottom + (Platform.OS === 'android' ? 8 : 0) }}>
      <ToastNotification />
      <View
        style={[
          styles.container,
          {
            paddingTop: insets.top,
            backgroundColor: isGamePaused ? DS.palette['Shades/Maroon 2'] : DS.palette['Brand/Maroon']
          }
        ]}
      >
        <WebWrapper>
          <TeacherControl.Panel />
          <TeacherControl.PlayerPanel />

          {!isWalkthrough ? (
            <>
              <StatusBar barStyle={'light-content'} />
              <LineProgress isLoading={showLoadingProgressBar} height={2} />
              <Shortcuts />
            </>
          ) : (
            <Top />
          )}
          <View style={styles.tableContainer}>
            <BlindsTable />
            <Table />
          </View>
          {!isNinePlayerInTable && (
            <View
              style={{
                width: '100%',
                height: teacherControlsButtonHeight
              }}
            />
          )}

          {playerHandHeight > 0 ? (
            <Animated.View
              onLayout={(event) => {
                setTeacherControlsButtonHeight(event.nativeEvent.layout.height)
              }}
              style={[styles.cardsContainer, teacherControllAnimatedOffset]}
            >
              <View style={[styles.teacherButtonsContainer]}>
                <TeacherControl.PauseButton />
                <TeacherControl.ToggleButton />
              </View>
            </Animated.View>
          ) : null}
          <View
            onLayout={(event) => {
              setPlayerHandeHeight(event.nativeEvent.layout.height)
            }}
            style={[
              styles.playerContainer,
              {
                paddingBottom,
                flex: getPlayerHandHeight(),
                backgroundColor: 'rgba(246, 242, 233, 1)'
              }
            ]}
          >
            {walkthroughType !== 'TableTour' && (
              <View
                style={[
                  styles.playerCardsContainer,
                  {
                    bottom: playerHandHeight
                  }
                ]}
              >
                {showPlayerCards ? (
                  <View
                    style={{
                      top: 10
                    }}
                  >
                    <PlayerCards cards={myPlayer?.cards} />
                  </View>
                ) : null}
              </View>
            )}

            <PlayerHand />
          </View>

          {walkthroughType === 'TableTour' && (
            <View
              style={[
                styles.playerCardsContainer,
                { bottom: verticalScale(24) * 3 * 2 + 30 },
                currentStep?.id === 'hole' ? { zIndex: 3 } : {}
              ]}
            >
              {showPlayerCards ? <PlayerCards cards={myPlayer?.cards} /> : null}
            </View>
          )}
          {isWalkthrough ? <WalkthroughOverlay /> : null}
          {isFocused ? renderEmojis : null}
          {/* Android specific fix for the modal that prevents user from playing */}
        </WebWrapper>
      </View>

      <View>
        <Modal
          visible={!!modalConfig}
          animationType="fade"
          transparent={true}
          style={Platform.OS === 'android' ? { flex: 1, margin: 0 } : {}}
        >
          <ModalContent config={modalConfig} />
        </Modal>
      </View>

      <GameSheetModals />
      <LiveVideoPreview />
      {isWeb && <AVPermissionsModal />}
    </View>
  )
})

const GameSheetModals = observer(() => {
  const {
    game: { isRaiseOrDealTraining }
  } = useStore()

  const {
    bottomModalContent,
    bottomSheetModalRef,
    snapPoints,
    enableDismissOnClose,
    closeBottomModal,
    onChangePosition,
    renderBackdrop,
    animatedPosition
  } = useGameBottomModal()

  const insets = useSafeAreaInsets()
  const maxWidth = useMaxWidth()
  // const reducedMotion = useReducedMotion()

  const hideHandleIndicator = useMemo(() => {
    const hideIndicatorIn: BottomModalType[] = [BottomModalTypes.GameStart]
    if (bottomModalContent?.kind !== undefined && hideIndicatorIn.includes(bottomModalContent.kind)) {
      return true
    }
    return false
  }, [bottomModalContent])

  const enableContentPanningGesture = useMemo(() => {
    const contentPanningGestureIn: BottomModalType[] = [BottomModalTypes.Chats]
    if (bottomModalContent?.kind !== undefined && contentPanningGestureIn.includes(bottomModalContent.kind)) {
      return false
    }
    return true
  }, [bottomModalContent])

  const FooterContent = useCallback(() => {
    if (bottomModalContent?.kind === BottomModalTypes.Chats) {
      return (
        <View
          style={{
            position: 'relative',
            top: -INPUT_HEIGHT
          }}
        >
          <CommandsContextProvider context="legacy">
            <ChatFooter {...bottomModalContent.props} />
          </CommandsContextProvider>
          <View
            style={{
              backgroundColor: palette.cream,
              position: 'absolute',
              zIndex: -1,
              top: 0,
              right: 0,
              left: 0,
              height: INPUT_HEIGHT + insets.bottom
            }}
          />
        </View>
      )
    }
    return null
  }, [bottomModalContent, insets])

  const renderFooter = useCallback(
    (props: ComponentProps<typeof BottomSheetFooter>) => (
      <BottomSheetFooter
        {...props}
        style={{
          backgroundColor: palette.cream
        }}
        bottomInset={insets.bottom}
      >
        <FooterContent />
      </BottomSheetFooter>
    ),
    [FooterContent, insets]
  )

  const renderHandle = useCallback(
    (props: BottomSheetHandleProps) => {
      if (bottomModalContent?.kind === BottomModalTypes.Chats) {
        return <ChatHandle {...props} />
      }
      return <BottomSheetHandle {...props} />
    },
    [bottomModalContent]
  )

  return (
    <BottomSheetModal
      animatedPosition={animatedPosition}
      ref={bottomSheetModalRef}
      name="game-sheet"
      index={isRaiseOrDealTraining ? 1 : 0}
      {...(bottomModalContent?.kind !== 'Bet' ? { snapPoints: snapPoints } : {})}
      backdropComponent={renderBackdrop}
      overrideReduceMotion={ReduceMotion.System}
      onDismiss={closeBottomModal}
      enableDynamicSizing={Boolean(bottomModalContent?.kind === 'Bet')}
      keyboardBehavior={Platform.OS === 'android' ? 'extend' : 'interactive'}
      keyboardBlurBehavior="none"
      android_keyboardInputMode="adjustResize"
      enableDismissOnClose={enableDismissOnClose}
      enableBlurKeyboardOnGesture={false}
      enableContentPanningGesture={enableContentPanningGesture}
      enableHandlePanningGesture={true}
      enableOverDrag
      enablePanDownToClose={true}
      handleComponent={renderHandle}
      footerComponent={renderFooter}
      onChange={(index) => {
        onChangePosition(index)
      }}
      handleStyle={styles.bottomSheetHandleStyle}
      handleIndicatorStyle={hideHandleIndicator ? { display: 'none' } : styles.bottomSheetIndicatorStyle}
      backgroundStyle={{ backgroundColor: palette.cream }}
      style={{
        marginHorizontal: isWeb ? 'auto' : undefined,
        maxWidth: maxWidth,
        width: '100%'
      }}
    >
      <BottomModalContent config={bottomModalContent} />
    </BottomSheetModal>
  )
})

const styles = StyleSheet.create({
  container: {
    flex: 1
  },
  teacherButtonsContainer: {
    //flex: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingHorizontal: DS.spacing[16],
    zIndex: 10,
    flexGrow: 1,
    //position: 'absolute',
    width: '100%',
    marginBottom: 16
  },
  tableContainer: {
    flex: 4,
    backgroundColor: 'transparent'
    //marginBottom: '-10%'
  },
  cardsContainer: {
    position: 'absolute',
    left: isWeb ? 0 : undefined,
    right: isWeb ? 0 : undefined,
    zIndex: 1
  },
  playerCardsContainer: {
    position: 'absolute',
    left: 0,
    //top: '0%',
    width: '100%'
    //marginTop: -(verticalScale(24) * 3 + 30)
  },
  playerContainer: {
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16
  },
  bottomSheetHandleStyle: {
    backgroundColor: palette.cream,
    // backgroundColor: '#f0f'
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16
  },
  bottomSheetIndicatorStyle: {
    backgroundColor: '#c4c2bb',
    height: 4,
    width: 35
  }
})
